'use client'

import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react'

export interface CartItem {
  id: string
  name: string
  price: number
  stock?: number // ✅ nuevo
  quantity: number
  image?: string
  weight?: number
  volumeCm3?: number
  slug?: string
}

export interface ShippingOption {
  id: string
  name: string
  price: number
  deliveryTime?: string
  postalCode?: string
}

export type DeliveryMethod = 'shipping' | 'pickup' | null

interface CartContextType {
  items: CartItem[]
  invalidItems: string[]
  addItem: (item: CartItem) => void
  removeItem: (id: string) => void
  updateQuantity: (id: string, quantity: number) => void
  clearCart: () => void
  itemCount: number
  total: number
  totalWeight: number
  deliveryMethod: DeliveryMethod
  setDeliveryMethod: (method: DeliveryMethod) => void
  selectedShipping: ShippingOption | null
  setSelectedShipping: (shipping: ShippingOption | null) => void
  totalWithShipping: number
  canCheckout: boolean
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([])
  const [invalidItems, setInvalidItems] = useState<string[]>([])
  const [deliveryMethod, setDeliveryMethod] =
    useState<DeliveryMethod>('shipping')
  const [selectedShipping, setSelectedShipping] =
    useState<ShippingOption | null>(null)

  const validateCartItems = async (loadedItems: CartItem[]) => {
    try {
      const ids = loadedItems.map(item => item.id)
      const response = await fetch('/api/products/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      })
      const data = await response.json()

      if (!data.validIds || !Array.isArray(data.validIds)) return

      const validItems = loadedItems.filter(item =>
        data.validIds.includes(item.id)
      )
      const removedItems = loadedItems.filter(
        item => !data.validIds.includes(item.id)
      )

      if (removedItems.length > 0) {
        setInvalidItems(removedItems.map(item => item.id))
        setItems(validItems)
        localStorage.setItem('cart', JSON.stringify(validItems))
      }
    } catch (error) {
      console.error('Error al validar productos del carrito:', error)
    }
  }

  useEffect(() => {
    const savedCart = localStorage.getItem('cart')
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart) as CartItem[]
        setItems(parsed)
        validateCartItems(parsed)
      } catch {
        localStorage.removeItem('cart')
      }
    }
  }, [])

  useEffect(() => {
    if (items.length > 0) {
      localStorage.setItem('cart', JSON.stringify(items))
    } else {
      localStorage.removeItem('cart')
    }
  }, [items])

  // ✅ Helpers (solo lógica interna; no cambia UI)
  const normalizeStock = (value: unknown): number | undefined => {
    return typeof value === 'number' && Number.isFinite(value)
      ? value
      : undefined
  }

  const clampQuantityByStock = (qty: number, stock?: number): number => {
    const safeQty = Number.isFinite(qty) ? qty : 0
    if (typeof stock === 'number') {
      return Math.min(Math.max(safeQty, 0), stock)
    }
    return Math.max(safeQty, 0)
  }

  const addItem = (item: CartItem) => {
    setItems(currentItems => {
      const existing = currentItems.find(i => i.id === item.id)

      const incomingStock = normalizeStock(item.stock)

      // Si viene con stock 0, no agregamos (evita estados inconsistentes)
      if (typeof incomingStock === 'number' && incomingStock <= 0) {
        return currentItems
      }

      if (existing) {
        const existingStock = normalizeStock(existing.stock)
        const effectiveStock =
          typeof existingStock === 'number' ? existingStock : incomingStock

        const nextQtyRaw = existing.quantity + item.quantity
        const nextQty = clampQuantityByStock(nextQtyRaw, effectiveStock)

        return currentItems.map(i =>
          i.id === item.id
            ? {
                ...i,
                quantity: nextQty,
                // si antes no tenía stock guardado y ahora sí, lo guardamos
                stock:
                  typeof existingStock === 'number'
                    ? existingStock
                    : incomingStock,
              }
            : i
        )
      }

      // Item nuevo: clamp por stock si existe
      const clampedQty = clampQuantityByStock(item.quantity, incomingStock)

      // Si queda 0, no lo agregamos
      if (clampedQty <= 0) return currentItems

      return [...currentItems, { ...item, quantity: clampedQty }]
    })
  }

  const removeItem = (id: string) => {
    setItems(currentItems => currentItems.filter(item => item.id !== id))
  }

  const updateQuantity = (id: string, quantity: number) => {
    setItems(currentItems => {
      const current = currentItems.find(item => item.id === id)
      if (!current) return currentItems

      const stock = normalizeStock(current.stock)

      // Si no hay stock (0), sacamos el item
      if (typeof stock === 'number' && stock <= 0) {
        return currentItems.filter(item => item.id !== id)
      }

      const nextQty = clampQuantityByStock(quantity, stock)

      if (nextQty <= 0) {
        return currentItems.filter(item => item.id !== id)
      }

      return currentItems.map(item =>
        item.id === id ? { ...item, quantity: nextQty } : item
      )
    })
  }

  const clearCart = () => {
    setItems([])
    setInvalidItems([])
    setDeliveryMethod('shipping')
    setSelectedShipping(null)
  }

  const itemCount = items.reduce((count, item) => count + item.quantity, 0)
  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  const totalWeight = items.reduce((weight, item) => {
    const w = item.weight || 500
    return weight + w * item.quantity
  }, 0)

  const shippingCost =
    deliveryMethod === 'pickup' ? 0 : selectedShipping?.price || 0

  const totalWithShipping = total + shippingCost

  const canCheckout =
    items.length > 0 &&
    (deliveryMethod === 'pickup' ||
      (deliveryMethod === 'shipping' && selectedShipping !== null))

  return (
    <CartContext.Provider
      value={{
        items,
        invalidItems,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        itemCount,
        total,
        totalWeight,
        deliveryMethod,
        setDeliveryMethod,
        selectedShipping,
        setSelectedShipping,
        totalWithShipping,
        canCheckout,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart debe usarse dentro de un CartProvider')
  }
  return context
}
